import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { sanitizeImageUrl } from '../utils/api';
import DiscordIcon from './DiscordIcon';
import ArrowIcon from './ArrowIcon';

const LANGS = [
  { code: 'de', label: 'Deutsch' },
  { code: 'en', label: 'English' },
];

const NAV_ITEMS = [
  { page: 'dashboard', icon: '🏠', labelKey: 'nav_overview' },
  { page: 'clan', icon: '🏰', labelKey: 'nav_clan_info' },
  { page: 'rules', icon: '📜', labelKey: 'nav_rules' },
  { page: 'info', icon: 'ℹ️', labelKey: 'nav_scoring' },
  { page: 'halloffame', icon: '🏆', labelKey: 'nav_hall_of_fame' },
  { page: 'events', icon: '📊', labelKey: 'nav_events' },
];

export default function MobileNav({ t, lang, setLang, activePage, onPageChange, clanHeader, discordInviteLink }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (langRef.current && !langRef.current.contains(e.target)) setLangOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleNav = (page) => {
    onPageChange(page);
    setMenuOpen(false);
  };

  return (
    <>
      {/* Top Bar */}
      <div id="topNav" className="md:hidden bg-bg-secondary border-b border-border px-4 py-3 flex items-center justify-between sticky top-0 z-50">
        <div id="topNavBrand" className="flex items-center gap-2 cursor-pointer min-w-0" onClick={() => { onPageChange('dashboard'); setMenuOpen(false); }} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && onPageChange('dashboard')}>
          {clanHeader.badge && (
            <img src={sanitizeImageUrl(clanHeader.badge)} className="w-6 h-6 rounded flex-shrink-0" alt="Clan Badge" />
          )}
          <h1 className="text-sm font-bold truncate max-w-[120px] text-text-primary">{clanHeader.name || '⚔️ Clan War'}</h1>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setMenuOpen(!menuOpen)} className="p-2 rounded-lg bg-bg-tertiary hover:bg-bg-secondary transition">
            <span>{menuOpen ? '✕' : '☰'}</span>
          </button>
        </div>
      </div>

      {/* Dropdown Menu */}
      <div id="mobileMenu" className={`md:hidden bg-bg-secondary border-b border-border overflow-hidden transition-all duration-300 ${menuOpen ? 'open' : ''}`}>
        <div className="p-4 space-y-2">
          {NAV_ITEMS.map(item => (
            <button
              key={item.page}
              onClick={() => handleNav(item.page)}
              className={`menu-btn-mobile flex items-center gap-3 w-full p-3 rounded-lg hover:bg-bg-tertiary ${activePage === item.page ? 'active' : ''}`}
              data-page={item.page}
            >
              <span className="text-lg">{item.icon}</span>
              <span>{t(item.labelKey)}</span>
            </button>
          ))}
          {discordInviteLink && (
            <a href={discordInviteLink} target="_blank" rel="noopener noreferrer" className="mobile-discord-link">
              <DiscordIcon className="w-5 h-5 flex-shrink-0" />
              <span>{t('sidebar_discord_server')}</span>
            </a>
          )}
          <div className="mobile-menu-divider" />
          <div className="mobile-footer-row">
            <div className="mobile-home-tooltip-wrapper">
              <Link to="/" onClick={() => setMenuOpen(false)} className="mobile-home-btn" title={t('sidebar_back_home')}>
                <ArrowIcon className="mobile-home-arrow" />
              </Link>
              <span className="mobile-home-tooltip">{t('sidebar_back_home')}</span>
            </div>
            <div className="mobile-lang-dropdown" ref={langRef}>
              <button onClick={() => setLangOpen(o => !o)} className="mobile-lang-btn compact">
                <span className="mobile-lang-icon">🌐</span>
                <span>{LANGS.find(l => l.code === lang)?.label || lang.toUpperCase()}</span>
              </button>
              {langOpen && (
                <div className="mobile-lang-menu">
                  {LANGS.map(l => (
                    <button
                      key={l.code}
                      onClick={() => { setLang(l.code); setLangOpen(false); }}
                      className={`mobile-lang-option${l.code === lang ? ' active' : ''}`}
                    >
                      {l.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
